import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Loader2, CheckCircle2, AlertCircle, ArrowRight, KeyRound } from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AffiliateLogin — magic-link login for the affiliate dashboard.
 *
 * Flow:
 *   1. Affiliate enters their email
 *   2. POST /api/affiliate/login/magic -> 200 always (anti-enumeration)
 *   3. We show "Check your inbox" regardless of whether the email
 *      actually matches a registered affiliate.
 */
const AffiliateLogin = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const errParam = params.get('err');

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/affiliate/login/magic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Something went wrong.');
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />
      <section className="relative pt-[110px] md:pt-[170px] pb-16 md:pb-28 px-6 overflow-hidden min-h-[640px] md:min-h-[760px]">
        <AltParallaxBackdrop />
        <div className="relative z-10 max-w-xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7">
              <KeyRound className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2.5} />
              Affiliate Dashboard
            </span>
            <h1 className="text-4xl md:text-7xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] alt-hero-glow">
              Sign in.{' '}
              <em className="italic font-light text-blue-600">No password.</em>
            </h1>
            <p className="mt-6 text-base md:text-xl text-[#0b1f3a]/75 leading-relaxed alt-hero-glow">
              Enter the email you used to apply. We&rsquo;ll send a one-time
              link that signs you in for 30 days.
            </p>
          </div>

          {(errParam === 'invalid' || errParam === 'expired') && (
            <div className="mb-6 flex items-start gap-3 bg-amber-100/95 ring-1 ring-amber-300 text-amber-900 rounded-2xl px-5 py-4 text-sm">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <span>
                {errParam === 'expired'
                  ? 'That sign-in link has expired (links last 10 minutes). Request a fresh one below.'
                  : 'That sign-in link is invalid or already used. Request a fresh one below.'}
              </span>
            </div>
          )}

          {status === 'success' ? (
            <div
              data-testid="affiliate-login-success"
              className="rounded-3xl bg-white/95 backdrop-blur-md ring-1 ring-black/5 shadow-xl p-8 md:p-10 text-center"
            >
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 ring-1 ring-emerald-200 flex items-center justify-center mb-5">
                <Mail className="w-6 h-6 text-emerald-600" strokeWidth={2} />
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-[#0b1f3a] mb-3">
                Check your inbox.
              </h3>
              <p className="text-[#0b1f3a]/70 max-w-md mx-auto leading-relaxed">
                If <strong className="text-[#0b1f3a]">{email}</strong> is a
                verified affiliate, we just sent a sign-in link. The link
                expires in 10 minutes.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="affiliate-login-form"
              className="rounded-3xl bg-white/95 backdrop-blur-md ring-1 ring-black/5 shadow-xl p-7 md:p-10"
            >
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-2 block">
                  Email
                </span>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="affiliate-login-email"
                  className="w-full bg-[#fafbfd] text-[#0b1f3a] placeholder-[#0b1f3a]/35 px-5 py-3.5 rounded-2xl ring-1 ring-black/10 focus:ring-blue-500 focus:bg-white outline-none transition"
                  placeholder="you@example.com"
                />
              </label>

              {errorMsg && (
                <div className="mt-5 flex items-start gap-3 bg-rose-50 ring-1 ring-rose-200 text-rose-700 rounded-2xl px-5 py-4 text-sm">
                  <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  <span>{errorMsg}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                data-testid="affiliate-login-submit"
                className="mt-7 w-full inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-[#0b1f3a] text-white font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending sign-in link…
                  </>
                ) : (
                  <>
                    Send me a sign-in link
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <p className="mt-5 text-center text-[#0b1f3a]/60 text-sm flex items-center justify-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                30-day session — no password to remember
              </p>
            </form>
          )}

          <p className="text-center text-[#0b1f3a]/55 text-sm mt-8">
            Not an affiliate yet?{' '}
            <Link to="/affiliate" className="underline hover:text-[#0b1f3a]">
              Apply for the partner program
            </Link>
            .
          </p>
        </div>
      </section>
      <AltFooter />
    </div>
  );
};

export default AffiliateLogin;
